/* eslint-disable no-unused-vars */
import { useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip, Row, Button } from 'react-bootstrap';
import { toast } from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import { useDispatch, useSelector } from 'react-redux';
import guessFormat from 'moment-guess';
import moment from 'moment';
import { loaderActions } from '../../../features/Loader/LoaderSlice';
import Swal from 'sweetalert2';
import API from '../../../Services/API';
import OrderLine from '../OrderLine/Index';
import { formatDate } from '../../../Helpers/date';
import {
  validateTransferLine,
  validatePurchaseLine,
  isSeafreightShipped
} from '../../../Helpers/line.validation';
import { useCurrentCompanyFullName, useCurrentCompanyID } from '../../../hooks/useCurrentCompany';
import { sortLinesByErrors } from '../../../Helpers/sort';
import { CheckRequireApproval } from '../../../Helpers/requireApproval';
import _ from 'lodash';
import { isPOEligibleToPost } from '../../../Helpers/postable';

function UploadSingleOrderLines(props) {
  const dispatch = useDispatch();
  const { vendorType } = useParams();
  const inputEl = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [uploadedLines, setUploadedLines] = useState([]);
  const [saveableLines, setSaveableLines] = useState([]);
  const [requireApprovalLines, setRequireApprovalLines] = useState([]);
  const [notApprovedLines, setNotApprovedLines] = useState([]);
  const { isFeedBackLoopEnabled } = useSelector((state) => state.general);

  const companyId = useCurrentCompanyID();
  const companyFullName = useCurrentCompanyFullName();

  const clickUpload = () => {
    let line1Text, fieldsList;
    if (props.type === 'purchase') {
      line1Text = `For #${props.documentNo} purchase order <u><b>${props.vendorType}</b></u> view`;
      if (props.vendorType.toLowerCase() === 'warehouse') {
        fieldsList = `<br /> <ol start="1" class="text-left" style="color: red">
        <li>Warehouse Confirm Quantity</li>
        <li>Warehouse Arrival Date</li>
        <li>Quantity Damaged</li>
      </ol>`;
      } else {
        fieldsList = `<br /> <ol start="1"  class="text-left" style="color: red">
        <li>Vendor Confirm Quantity</li>
        <li>Vendor Shipment Number</li>
        <li>Root Cause</li>
        <li>Expected GRD</li>
        <li>Country Of Origin (required)</li>
        <li>Confirmed Unit Price(if not saved yet)</li>
      </ol>`;
      }
    } else {
      line1Text = `For #${props.documentNo} Transfer order`;
      fieldsList = `<br /> <ol start="1"  class="text-left" style="color: red">
      <li>Quantity Received</li>
      <li>Warehouse Arrival Date</li>
      <li>Quantity Damaged</li>
    </ol>`;
    }
    Swal.fire({
      title: 'Mass Upload info',
      icon: 'info',
      html: `${line1Text}, Only following fields are updatable: <br />` + `${fieldsList} `,
      showCloseButton: true,
      showCancelButton: true,
      focusConfirm: false,
      confirmButtonText: '<i class="feather icon-thumbs-up"></i> Great!',
      confirmButtonAriaLabel: 'Thumbs up, great!',
      cancelButtonText: '<i class="feather icon-thumbs-down"></i>',
      cancelButtonAriaLabel: 'Thumbs down'
    }).then((result) => {
      if (result.isConfirmed) {
        inputEl.current.click();
      }
    });
  };

  const toggleDrawer = () => {
    setIsOpen((prevState) => !prevState);
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      if (props.type === 'purchase') {
        PurchaseExcelToJSON(e.target.files[0]);
      } else {
        TransferExcelToJSON(e.target.files[0]);
      }
    }
    e.target.value = null;
  };

  async function validateLines(actionType) {
    let validatedLines = [];
    if (props.type === 'purchase') {
      validatedLines = uploadedLines.map((line) =>
        validatePurchaseLine(line, vendorType, uploadedLines, isFeedBackLoopEnabled)
      );
    } else {
      validatedLines = uploadedLines.map((line) => validateTransferLine(line));
    }
    setUploadedLines(sortLinesByErrors(validatedLines));
    if (validatedLines.filter((line) => !line.isValid).length === 0) {
      let result;
      let showPopup = false;

      if (props.vendorType === 'Warehouse') {
        const quantityConfirmation = validatedLines.filter((line) => {
          return CheckRequireApproval(line, isFeedBackLoopEnabled) === true;
        });
        if (quantityConfirmation.length > 0) {
          showPopup = true;
        }
      }

      if (isFeedBackLoopEnabled && showPopup) {
        result = await Swal.fire({
          title: 'The amount is over and the PO will need to be approved',
          text: "Please make sure everything is uploaded properly, You won't be able to revert this!",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: `Yes, ${actionType} it!`,
          customClass: {
            title: 'swal2-title-red'
          }
        });
      } else {
        result = await Swal.fire({
          title: 'Are you sure?',
          text: "Please make sure everything is uploaded properly, You won't be able to revert this!",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: `Yes, ${actionType} it!`
        });
      }
      if (result.isConfirmed) {
        if (actionType.toLowerCase() === 'save') {
          confirmSaveLines();
        } else {
          confirmPostLines();
        }
      }
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Please fix all lines invalid inputs...'
      });
    }
  }

  function confirmSaveLines() {
    if (props.type === 'purchase') {
      savePurchaseLine(false);
    } else {
      saveTransferLine(false);
    }
  }

  function confirmPostLines() {
    if (props.type === 'purchase') {
      savePurchaseLine(true);
    } else {
      saveTransferLine(true);
    }
  }

  async function savePurchaseLine(alsoPost = false) {
    dispatch(loaderActions.show());
    const batchRequestData = saveableLines.map((line) => {
      let data = {};
      if (props.vendorType.toLowerCase() === 'warehouse') {
        data = {
          actualReceiptDate: line.actualReceiptDate,
          qtyDmged: Number(line.qtyDmged),
          qtyMissing: Number(line.qtyMissing),
          SkipPriceValidation: Boolean(line.SkipPriceValidation)
        };
        if (line.vpFreightType === 'SeaRailAir') {
          data = {
            ...data,
            seaFreightConfirmedQty: Number(line.seaFreightConfirmedQty)
          };
        } else {
          data = {
            ...data,
            whseConfirmQty: Number(line.whseConfirmQty)
          };
        }
      } else {
        data = {
          vndrShpmntNo: String(line.vndrShpmntNo),
          vendorConfirmQty: Number(line.vendorConfirmQty),
          expectedReceiptDate: line.expectedReceiptDate,
          vpFreightType: line.vpFreightType === 'SeaRailAir' ? 'SeaRailAir' : 'TruckFreight',
          lossReason: line.lossReason,
          mfgOriginCountry: line.mfgOriginCountry,
          vpDirectUnitCost: line.vpDirectUnitCost
        };
      }
      return {
        method: 'PATCH',
        url: `purchaseOrderLinesVP('${line.documentNo}',${line.lineNo},'${line.itemNo}')`,
        headers: {
          Company: companyFullName,
          'Content-Type': 'application/json',
          'If-Match': '*'
        },
        body: data
      };
    });
    const res = await API().post('/purchase-batch', { requests: batchRequestData });
    const errors = [];
    res.data.responses.forEach((resp) => {
      if (resp.body && typeof resp.body === 'object' && resp.body.error) {
        errors.push(resp.body.error.message);
      }
    });
    if (errors.length > 0) {
      toggleDrawer();
      dispatch(loaderActions.hide());
      errors.map((err) => toast.error(err));
    } else if (alsoPost === false) {
      toggleDrawer();
      dispatch(loaderActions.hide());
      toast.success('Order lines saved successfully');
      props.refresh();
    }

    if (alsoPost === true) {
      const res = await API().post(`/warehouse_post?(${companyId})?('${props.documentNo}')`, {
        postDate: formatDate(new Date())
      });
      if (res.data && typeof res.data === 'object' && res.data.error) {
        toggleDrawer();
        dispatch(loaderActions.hide());
        toast.error(res.data.error.message);
      } else if (res.response && res.response.status >= 400) {
        toggleDrawer();
        dispatch(loaderActions.hide());
        toast.error(res.response.data.message);
      } else if (res.code === 'ERR_NETWORK') {
        toast.error(res.message);
      } else {
        toggleDrawer();
        dispatch(loaderActions.hide());
        toast.success('Order lines posted successfully');
        props.refresh();
      }
    }
  }

  async function saveTransferLine(alsoPost = false) {
    dispatch(loaderActions.show());
    const batchRequestData = uploadedLines.map((line) => {
      const data = {
        actualReceiptDate: line.actualReceiptDate,
        qtyDmged: Number(line.qtyDmged),
        qtyMissing: Number(line.qtyMissing),
        qtyToReceive: Number(line.qtyToReceive)
      };

      return {
        method: 'PATCH',
        url: `transferOrderLinesVP('${line.documentNo}',${line.lineNo},'${line.itemNo}')`,
        headers: {
          Company: companyFullName,
          'Content-Type': 'application/json',
          'If-Match': '*'
        },
        body: data
      };
    });
    const res = await API().post('/transfer-batch', { requests: batchRequestData });
    const errors = [];
    res.data.responses.forEach((resp) => {
      if (resp.body && typeof resp.body === 'object' && resp.body.error) {
        errors.push(resp.body.error.message);
      }
    });
    if (errors.length > 0) {
      errors.map((err) => toast.error(err));
    } else if (alsoPost === false) {
      toast.success('Order lines saved successfully');
    }
    if (alsoPost === true) {
      const res = await API().post(`/transfer_post?(${companyId})?('${props.documentNo}')`, {
        postDate: formatDate(new Date())
      });
      if (res.data && typeof res.data === 'object' && res.data.error) {
        toast.error(res.data.error.message);
      } else if (res.response && res.response.status >= 400) {
        toast.error(res.response.data.message);
      } else if (res.code === 'ERR_NETWORK') {
        toast.error(res.message);
      } else {
        toast.success('Order lines posted successfully');
      }
    }
    dispatch(loaderActions.hide());
    props.refresh();
    toggleDrawer();
  }

  function ExcelDateToJSDate(serial) {
    const utcDays = Math.floor(serial - 25569);
    const utcValue = utcDays * 86400;
    const dateInfo = new Date(utcValue * 1000);
    return `${dateInfo.getFullYear()}-${(dateInfo.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${dateInfo.getDate().toString().padStart(2, '0')}`;
  }

  function ExcelDatetoEVPFormat(date) {
    if (date) {
      const currentFormat = guessFormat(date);
      return moment(date, currentFormat).format('YYYY-MM-DD');
    } else {
      return date;
    }
  }

  const PurchaseExcelToJSON = (file) => {
    const reader = new FileReader();
    reader.onload = function (e) {
      const binaryString = e.target.result;
      // convert the binary string to a workbook
      const workbook = XLSX.read(binaryString, { type: 'binary' });
      // get the first sheet
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      // convert the sheet to JSON
      const rows = XLSX.utils.sheet_to_json(sheet);
      const rowsData = rows.filter((row) => row['Order No'] === props.documentNo);
      if (rowsData.length === 0) {
        toast.error(`No Line matching for order ${props.documentNo}.`);
      } else {
        let lines = rowsData.map((line) => {
          const targetLine = props.orderLines.find((l) => l.lineNo === line['Line No']);
          if (vendorType.toLowerCase() === 'supplier') {
            // Expected GRD
            if (
              line['Expected GRD (yyyy/mm/dd)'] &&
              typeof line['Expected GRD (yyyy/mm/dd)'] === 'number'
            ) {
              line['Expected GRD (yyyy/mm/dd)'] = ExcelDateToJSDate(
                line['Expected GRD (yyyy/mm/dd)']
              );
            } else {
              line['Expected GRD (yyyy/mm/dd)'] = ExcelDatetoEVPFormat(
                line['Expected GRD (yyyy/mm/dd)']
              );
            }

            let vpDirectUnitCost = '';
            if (
              targetLine.editedFields.includes('vpDirectUnitCost') &&
              line['Confirmed Unit Price'] &&
              !isNaN(Number(line['Confirmed Unit Price']))
            ) {
              vpDirectUnitCost = Number(line['Confirmed Unit Price']);
            } else if (targetLine.vpDirectUnitCost) {
              vpDirectUnitCost = targetLine.vpDirectUnitCost;
            }

            return {
              documentNo: line['Order No'],
              lineNo: line['Line No'],
              itemNo: line['Item No'],
              description: line.Description,
              vndrShpmntNo: String(line['Vendor Shipment Number']),
              locationCode: line['Location Code'],
              quantity: Number(line.Quantity),
              vendorConfirmQty: Number(line['Vendor Confirm Quantity']),
              vpFreightType: targetLine.vpFreightType,
              expectedReceiptDate: line['Expected GRD (yyyy/mm/dd)'],
              lossReason: line['Root Cause'],
              whseConfirmQty: Number(targetLine.whseConfirmQty),
              seaFreightConfirmedQty: Number(targetLine.seaFreightConfirmedQty),
              actualReceiptDate: targetLine.actualReceiptDate,
              actualShippingDate: targetLine.actualShippingDate,
              qtyDmged: Number(targetLine.qtyDmged),
              qtyReceived: Number(targetLine.qtyReceived),
              qtyMissing: Number(targetLine.qtyMissing),
              mfgOriginCountry: line['Country Of Origin'],
              vpDirectUnitCost,
              isValid: true,
              editedFields: [],
              validationErrors: [],
              validationWarnings: [],
              vendorPortalStatus: targetLine.vendorPortalStatus,
              vendorPortalStatusCaption: targetLine.vendorPortalStatusCaption,
              feedbackDate: targetLine.feedbackDate,
              feedbackStatus: targetLine.feedbackStatus,
              feedbackType: targetLine.feedbackType,
              feedbackUser: targetLine.feedbackUser,
              overReceiptCode: targetLine.overReceiptCode,
              originalQuantity: targetLine.originalQuantity,
              directUnitCost: targetLine.directUnitCost,
              plannedReceiptDate: targetLine.plannedReceiptDate,
              expectedDeliveryDate: targetLine.expectedDeliveryDate,
              isVpDirectUnitCostEditable:
                !targetLine.vpDirectUnitCost || targetLine.editedFields.includes('vpDirectUnitCost')
            };
          } else {
            if (typeof line['Warehouse Arrival Date (yyyy/mm/dd)'] === 'number') {
              line['Warehouse Arrival Date (yyyy/mm/dd)'] = ExcelDateToJSDate(
                line['Warehouse Arrival Date (yyyy/mm/dd)']
              );
            } else {
              line['Warehouse Arrival Date (yyyy/mm/dd)'] = ExcelDatetoEVPFormat(
                line['Warehouse Arrival Date (yyyy/mm/dd)']
              );
            }
            let qtyMissing =
              targetLine.vendorConfirmQty - Number(line['Warehouse Confirm Quantity']);
            if (qtyMissing < 0) {
              qtyMissing = 0;
            }
            let wareHouseQty = {};
            if (targetLine.vpFreightType === 'SeaRailAir') {
              wareHouseQty = {
                seaFreightConfirmedQty: Number(line['Warehouse Confirm Quantity'])
              };
            } else {
              wareHouseQty = {
                whseConfirmQty: Number(line['Warehouse Confirm Quantity'])
              };
            }
            return {
              documentNo: line['Order No'],
              id: `${line['Order No']}_${line['Line No']}`,
              lineNo: line['Line No'],
              itemNo: line['Item No'],
              description: line.Description,
              vndrShpmntNo: String(targetLine.vndrShpmntNo),
              locationCode: line['Location Code'],
              quantity: Number(line.Quantity),
              vendorConfirmQty: Number(targetLine.vendorConfirmQty),
              lossReason: line.lossReason,
              seaFreightConfirmedQty: targetLine.seaFreightConfirmedQty,
              vpFreightType: targetLine.vpFreightType,
              expectedReceiptDate: targetLine.expectedReceiptDate,
              plannedReceiptDate: targetLine.plannedReceiptDate,
              expectedDeliveryDate: targetLine.expectedDeliveryDate,
              actualShippingDate: targetLine.actualShippingDate,
              vendorPortalStatus: targetLine.vendorPortalStatus,
              vendorPortalStatusCaption: targetLine.vendorPortalStatusCaption,
              ...wareHouseQty,
              actualReceiptDate: line['Warehouse Arrival Date (yyyy/mm/dd)'],
              qtyDmged: Number(line['Quantity Damaged']),
              qtyReceived: Number(targetLine.qtyReceived),
              qtyMissing,
              isValid: true,
              editedFields: [],
              validationErrors: [],
              validationWarnings: [],
              feedbackDate: targetLine.feedbackDate,
              feedbackStatus: targetLine.feedbackStatus,
              feedbackType: targetLine.feedbackType,
              feedbackUser: targetLine.feedbackUser,
              overReceiptCode: targetLine.overReceiptCode,
              originalQuantity: targetLine.originalQuantity,
              directUnitCost: targetLine.directUnitCost
            };
          }
        });
        lines = _.uniqBy(lines, 'lineNo');
        setUploadedLines(lines);
        toggleDrawer();
      }
    };

    reader.onerror = function (ex) {
      console.log(ex);
    };
    reader.readAsBinaryString(file);
  };

  const TransferExcelToJSON = (file) => {
    const reader = new FileReader();
    reader.onload = function (e) {
      const binaryString = e.target.result;
      // convert the binary string to a workbook
      const workbook = XLSX.read(binaryString, { type: 'binary' });
      // get the first sheet
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      // convert the sheet to JSON
      const rows = XLSX.utils.sheet_to_json(sheet);
      const rowsData = rows.filter((row) => row['Order No'] === props.documentNo);
      if (rowsData.length === 0) {
        toast.error(`No Line matching for order ${props.documentNo}.`);
      } else {
        const lines = rowsData.map((line) => {
          const targetLine = props.orderLines.find((l) => l.lineNo === line['Line No']);
          let qtyMissing =
            targetLine.itemQty - (targetLine.qtyShipped + Number(line['Quantity Received']));
          if (qtyMissing < 0) {
            qtyMissing = 0;
          }

          if (typeof line['Warehouse Arrival Date (yyyy/mm/dd)'] === 'number') {
            line['Warehouse Arrival Date (yyyy/mm/dd)'] = ExcelDateToJSDate(
              line['Warehouse Arrival Date (yyyy/mm/dd)']
            );
          }
          return {
            documentNo: line['Order No'],
            lineNo: line['Line No'],
            itemNo: line['Item No'],
            description: line.Description,
            itemQty: Number(targetLine.itemQty),
            expectedReceiptDate: targetLine.expectedReceiptDate,
            qtyShipped: Number(targetLine.qtyShipped),
            vendorPortalStatus: targetLine.vendorPortalStatus,
            vendorPortalStatusCaption: targetLine.vendorPortalStatusCaption,
            qtyToReceive: Number(line['Quantity Received']),
            actualReceiptDate: line['Warehouse Arrival Date (yyyy/mm/dd)'],
            qtyDmged: Number(line['Quantity Damaged']),
            qtyMissing: Number(qtyMissing),
            mfgOriginCountry: targetLine.mfgOriginCountry,
            isValid: true,
            editedFields: [],
            validationErrors: [],
            validationWarnings: []
          };
        });
        setUploadedLines(lines);
        toggleDrawer();
      }
    };

    reader.onerror = function (ex) {
      console.log(ex);
    };
    reader.readAsBinaryString(file);
  };

  function editValue(documentNo, lineNo, targetField) {
    const targetKey = Object.keys(targetField)[0];
    setUploadedLines((prevState) => {
      return prevState.map((line) => {
        if (lineNo === line.lineNo) {
          line[targetKey] = targetField[targetKey];
        }
        return line;
      });
    });
    if (
      (targetKey === 'whseConfirmQty' && targetField[targetKey] > 0) ||
      (targetKey === 'seaFreightConfirmedQty' && targetField[targetKey] > 0)
    ) {
      setUploadedLines((prevState) => {
        return prevState.map((line) => {
          if (lineNo === line.lineNo) {
            let qtyMissing = line.vendorConfirmQty - (line.qtyReceived + targetField[targetKey]);
            if (qtyMissing < 0) {
              qtyMissing = 0;
            }
            line.qtyMissing = qtyMissing;
          }
          return line;
        });
      });
    }
    if (targetKey === 'qtyToReceive' && targetField[targetKey] > 0) {
      setUploadedLines((prevState) => {
        return prevState.map((line) => {
          if (lineNo === line.lineNo) {
            let qtyMissing = line.itemQty - (line.qtyShipped + targetField[targetKey]);
            if (qtyMissing < 0) {
              qtyMissing = 0;
            }
            line.qtyMissing = qtyMissing;
          }
          return line;
        });
      });
    }
  }

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.removeProperty('overflow');
    }
  }, [isOpen]);

  useEffect(() => {
    const saveableLines = (
      isFeedBackLoopEnabled
        ? uploadedLines.filter((line) => line.feedbackStatus !== 'Pending')
        : uploadedLines
    ).filter((line) => !isSeafreightShipped(line));
    setSaveableLines(saveableLines);

    const requireApprovalLines = isFeedBackLoopEnabled
      ? uploadedLines.filter((line) => {
          return CheckRequireApproval(line, isFeedBackLoopEnabled) === true;
        })
      : [];

    setRequireApprovalLines(requireApprovalLines);

    const notApprovedLines = isFeedBackLoopEnabled
      ? requireApprovalLines.filter((line) => line.feedbackStatus !== 'Approved')
      : [];

    setNotApprovedLines(notApprovedLines);
  }, [uploadedLines]);

  const uploadedRows = uploadedLines.map((line, index) => (
    <OrderLine
      documentNo={line.documentNo}
      line={line}
      index={index}
      vendorType={vendorType}
      key={index}
      type={props.type}
      editValue={editValue}
      showSaveButton={false}
      showOrderLink={false}
      orderLines={props.orderLines}
      isBackendLines={true}
    />
  ));

  return (
    <>
      <OverlayTrigger
        key="upload"
        className="mr-2 "
        placement={'top'}
        overlay={
          <Tooltip id={`tooltip-upload`} role={'button'}>
            Upload
          </Tooltip>
        }
      >
        <i className="feather icon-upload ml-2 mt-2" type="button" onClick={clickUpload} />
      </OverlayTrigger>

      <Drawer
        open={isOpen}
        onClose={toggleDrawer}
        direction="right"
        size={'80vw'}
        style={{
          zIndex: '10000',
          overflowX: 'hidden',
          overflowY: 'scroll',
          paddingBottom: '10vh'
        }}
      >
        <div className="p-3">
          <div className="row">
            <div className="col align-self-start">Review Upload</div>
            <div className="col align-self-end text-right" role={'button'}>
              <i
                className="feather icon-x-circle ml-2 mt-2"
                style={{ fontSize: '150%', color: 'red' }}
                onClick={toggleDrawer}
              />
            </div>
          </div>
          <Row className="mt-2 px-2" style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Button variant="info" onClick={() => validateLines('Save')}>
              Save Lines ({saveableLines.length})
            </Button>
            {isPOEligibleToPost(
              vendorType,
              props.currentOrder,
              uploadedLines,
              requireApprovalLines,
              notApprovedLines
            ) ? (
              <Button variant="primary" className="ml-2" onClick={() => validateLines('Post')}>
                Post PO #{props.documentNo}
              </Button>
            ) : (
              <></>
            )}
          </Row>
        </div>
        <div className="row mt-2 px-4">{uploadedRows}</div>
        <Row className="mt-4 px-4" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="info" onClick={() => validateLines('Save')}>
            Save Lines ({saveableLines.length})
          </Button>
          {isPOEligibleToPost(
            vendorType,
            props.currentOrder,
            uploadedLines,
            requireApprovalLines,
            notApprovedLines
          ) ? (
            <Button variant="primary" className="ml-2" onClick={() => validateLines('Post')}>
              Post PO #{props.documentNo}
            </Button>
          ) : (
            <></>
          )}
        </Row>
      </Drawer>
      <input
        type="file"
        ref={inputEl}
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.csv"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </>
  );
}

export default UploadSingleOrderLines;
